import React, { useEffect, useState, useRef } from 'react'
import styles from './LockScreen.module.css'
import MusicPlayer from '../../components/MusicPlayer/MusicPlayer'
import { translations, getCurrentLanguage } from '../../translations'
import { useTranslationUpdate } from '../../hooks/useTranslationUpdate'

interface LockScreenProps {
  onUnlock: () => void
}

export default function LockScreen({ onUnlock }: LockScreenProps) {
  useTranslationUpdate()
  const currentLang = getCurrentLanguage()
  
  const [time, setTime] = useState('')
  const [date, setDate] = useState('')
  const lockRef = useRef<HTMLDivElement>(null)
  const startY = useRef<number | null>(null)
  const [translateY, setTranslateY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const hours = now.getHours().toString().padStart(2, '0')
      const minutes = now.getMinutes().toString().padStart(2, '0')
      setTime(`${hours}:${minutes}`)

      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }

      const formatted = now.toLocaleDateString(currentLang, options)
      setDate(formatted.charAt(0).toUpperCase() + formatted.slice(1))
    }

    updateTime()
    const interval = setInterval(updateTime, 1000)
    return () => clearInterval(interval)
  }, [currentLang])

  const handleStart = (y: number) => {
    startY.current = y
    setIsDragging(true)
  }

  const handleMove = (y: number) => {
    if (!isDragging || startY.current === null) return
    const delta = y - startY.current
    if (delta < 0) {
      setTranslateY(delta)
    }
  }

  const handleEnd = () => {
    if (translateY < -100) {
      setTranslateY(-window.innerHeight)
      setTimeout(() => {
        onUnlock()
      }, 300)
    } else {
      setTranslateY(0)
    }
    setIsDragging(false)
    startY.current = null
  }

  return (
    <div
      ref={lockRef}
      className={styles.lockscreen}
      style={{ transform: `translateY(${translateY}px)` }}
      onMouseDown={(e) => handleStart(e.clientY)}
      onMouseMove={(e) => handleMove(e.clientY)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={(e) => handleStart(e.touches[0].clientY)}
      onTouchMove={(e) => handleMove(e.touches[0].clientY)}
      onTouchEnd={handleEnd}
    >
      <div className={styles.centerBlock}>
        <div className={styles.timeBlock}>
          <p className={styles.date}>{date}</p>
          <h1 className={styles.time}>{time}</h1>
        </div>

        <div className={styles.musicPlayerWrapper} onClick={(e) => e.stopPropagation()}>
          <MusicPlayer />
        </div>
      </div>

      <div className={styles.bottom}>
        <p className={styles.swipeText}>{translations('lock_screen.swipe_to_unlock')}</p>
      </div>
    </div>
  )
}
